import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Target, Users, Handshake } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const About = () => {
  const { t } = useLanguage();

  const valueIcons = [Shield, Target, Users, Handshake];
  const values = t('about.values.items');

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-semibold text-[#1D1D1F] tracking-tight leading-[1.1] mb-6 font-['Plus_Jakarta_Sans']">
              {t('about.title')}
            </h1>
            <p className="text-lg md:text-xl text-[#86868B] leading-relaxed">
              {t('about.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <p className="text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
                {t('about.mission.title')}
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-6 font-['Plus_Jakarta_Sans']">
                {t('hero.tagline')}
              </h2>
              <p className="text-lg text-[#86868B] leading-relaxed">
                {t('about.mission.text')}
              </p>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-[#F5F5F7]">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
              {t('about.values.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.isArray(values) && values.map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <motion.div
                  key={value.title}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-3xl border border-slate-100 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[#007AFF10] flex items-center justify-center mb-6">
                    <Icon className="w-6 h-6 text-[#007AFF]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1D1D1F] mb-3 font-['Plus_Jakarta_Sans']">
                    {value.title}
                  </h3>
                  <p className="text-[#86868B] leading-relaxed">
                    {value.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EU Focus Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              {...fadeUp}
              className="order-2 lg:order-1"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-[#F5F5F7]">
                <img
                  src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=800&q=80"
                  alt="Modern office"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <p className="text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
                {t('about.euFocus.title')}
              </p>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-6 font-['Plus_Jakarta_Sans']">
                Built for Europe
              </h2>
              <p className="text-lg text-[#86868B] leading-relaxed mb-8">
                {t('about.euFocus.text')}
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#F5F5F7] text-sm font-medium text-[#1D1D1F]">
                  GDPR Compliant
                </span>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#F5F5F7] text-sm font-medium text-[#1D1D1F]">
                  EU Data Centers
                </span>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#F5F5F7] text-sm font-medium text-[#1D1D1F]">
                  Local Support
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1D1D1F] tracking-tight mb-6 font-['Plus_Jakarta_Sans']">
              Victory Service Provider LTD
            </h2>
            <p className="text-lg text-[#86868B] leading-relaxed mb-8">
              20-22 Wenlock Road<br />
              London N1 7GU, UK
            </p>
            <Link to="/contact">
              <Button
                className="bg-[#1D1D1F] text-white hover:bg-black rounded-full px-10 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
                data-testid="about-cta-btn"
              >
                {t('nav.contact')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
